// src/encounter/intelligence/use-next-step.ts
//
// The Next step card's requests, on the same rules as the Explain card (use-explain.ts):
//   - auth: the signed-in clinician's own token (authStorage), read at call time;
//   - A2: the payload is checked on arrival; a malformed one is the technical line, never the body;
//   - A7/A12: one card request in flight; a reply is dropped unless it answers the open request for the open patient;
//     the card and every decision state clear the moment the patient or encounter changes;
//   - nothing logs: no patient id, payload or error text reaches a console or telemetry.
//
// Decisions (POST /api/intelligence/decisions): one request in flight per item. Each distinct press makes a new
// decisionId (the idempotency key); a retry after "audit log unavailable" or a network failure resends the SAME id and
// body, so the engine can never record one press twice. "Not indicated" is never sent without a reason.
import { useCallback, useEffect, useRef, useState } from 'react';
import { authStorage } from '@/auth/storage';
import { INTELLIGENCE_BASE } from '../intelligence-engine';
import { SESSION_EXPIRED, STATE_LINE } from './explanation';
import { newRequestId } from './use-explain';
import { withTransientRetry } from './engine-retry';
import { DECISION_LINE, REASON_MAX, isRecorded, parseDecisions, parseNextStep, type DecisionAction, type DecisionRequest, type NextStepV1, type RecordedDecision } from './next-step';

export const NEXT_STEP_PATH = `${INTELLIGENCE_BASE}/cards/next-step`;
export const DECISIONS_PATH = `${INTELLIGENCE_BASE}/decisions`;

type FetchLike = (input: string, init: RequestInit) => Promise<Response>;

export type DecisionStatus = 'sending' | 'recorded' | 'conflict' | 'retryable';

export interface DecisionState {
  status: DecisionStatus;
  /** The body sent (and resent on retry): its decisionId is the idempotency key. */
  request: DecisionRequest;
  /** The fixed line under the item, or null while sending. */
  line: string | null;
}

export interface NextStepView {
  status: 'idle' | 'loading' | 'done';
  card: NextStepV1 | null;
  notice: string | null;
  decisions: Record<string, DecisionState>;
}

const IDLE: NextStepView = { status: 'idle', card: null, notice: null, decisions: {} };

export interface UseNextStepOptions {
  patientId: string | null | undefined;
  encounterId?: string | null;
  fetch?: FetchLike;
  token?: () => string | null;
  /** Test seam for the decisionId. */
  newId?: () => string;
  /** Test seam for decidedAt. */
  now?: () => Date;
}

const headers = (access: string) => ({ authorization: `Bearer ${access}`, 'content-type': 'application/json', accept: 'application/json' });

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** The engine accepts a decidedAt up to 10 minutes old; a resend older than this first asks the engine what it holds. */
export const DECIDED_AT_STALE_MS = 9 * 60_000;

/** Only the presses the engine has recorded: they are the record and survive a reload. */
function recordedOnly(decisions: Record<string, DecisionState>): Record<string, DecisionState> {
  return Object.fromEntries(Object.entries(decisions).filter(([, d]) => d.status === 'recorded'));
}

/** Presses not yet confirmed recorded keep their request (and so their decisionId) across a reload. */
function unresolvedOnly(decisions: Record<string, DecisionState>): Record<string, DecisionState> {
  return Object.fromEntries(Object.entries(decisions).filter(([, d]) => d.status !== 'recorded'));
}

function toRequest(d: RecordedDecision, patientId: string): DecisionRequest {
  return { decisionId: d.decisionId, patientId, encounterId: d.encounterId, card: 'next-step', itemId: d.itemId, action: d.action, ...(d.reason ? { reason: d.reason } : {}), decidedAt: d.decidedAt };
}

/** What a decision reply becomes. Pure, so the hook and its tests read one rule. */
export function decisionOutcome(status: number, body: unknown, decisionId: string): { status: DecisionStatus; line: string | null } {
  if (status === 200 && isRecorded(body, decisionId)) return { status: 'recorded', line: null };
  if (status === 409) return { status: 'conflict', line: DECISION_LINE.conflict };
  if (status === 403) return { status: 'conflict', line: DECISION_LINE.refused };
  if (status === 503) return { status: 'retryable', line: DECISION_LINE.auditDown };
  if (status === 401) return { status: 'retryable', line: SESSION_EXPIRED };
  return { status: 'retryable', line: DECISION_LINE.technical };
}

export function useNextStep({ patientId, encounterId = null, fetch: fetchImpl, token, newId = newRequestId, now = () => new Date() }: UseNextStepOptions) {
  const context = `${patientId ?? ''}|${encounterId ?? ''}`;
  const [kept, setView] = useState<NextStepView & { context: string }>({ ...IDLE, context });
  if (kept.context !== context) setView({ ...IDLE, context });
  const view: NextStepView = kept.context === context ? kept : IDLE;
  const cardFlight = useRef<AbortController | null>(null);
  const decisionFlights = useRef(new Map<string, AbortController>());

  // A7: a patient or encounter change (and unmount) cancels every request in flight.
  useEffect(
    () => () => {
      cardFlight.current?.abort();
      cardFlight.current = null;
      decisionFlights.current.forEach((ac) => ac.abort());
      decisionFlights.current.clear();
    },
    [patientId, encounterId],
  );

  const access = useCallback(() => (token ?? (() => authStorage.getAccessToken()))(), [token]);
  const doFetch = useCallback((input: string, init: RequestInit) => (fetchImpl ?? ((i: string, n: RequestInit) => fetch(i, n)))(input, init), [fetchImpl]);

  /** GET the decisions already recorded for this patient in this encounter (newest first), or null on any failure. */
  const readBack = useCallback(
    async (tok: string, signal: AbortSignal): Promise<RecordedDecision[] | null> => {
      if (!patientId) return null;
      try {
        const res = await doFetch(`${DECISIONS_PATH}?patientId=${encodeURIComponent(patientId)}`, { method: 'GET', headers: headers(tok), signal });
        const list = res.status === 200 ? parseDecisions(await readJson(res)) : null;
        return list ? list.filter((d) => d.card === 'next-step' && d.encounterId === (encounterId ?? null)) : null;
      } catch {
        return null;
      }
    },
    [patientId, encounterId, doFetch],
  );

  const load = useCallback(async () => {
    if (!patientId || cardFlight.current) return; // A12: one card request in flight.
    // A reload while a decision is in flight could land an older read-back over the press it has not seen yet.
    if (decisionFlights.current.size > 0) return;
    const tok = access();
    if (!tok) {
      setView({ ...IDLE, status: 'done', notice: SESSION_EXPIRED, context });
      return;
    }
    const ac = new AbortController();
    cardFlight.current = ac;
    // A reload starts a fresh card: earlier decision lines belong to the card that was replaced. A press the engine
    // has already recorded stays recorded (it is the record), so its buttons can never reopen.
    setView((v) => ({ ...IDLE, status: 'loading', context, decisions: v.context === context ? v.decisions : {} }));
    let card: NextStepV1 | null = null;
    let notice: string | null = null;
    try {
      // engine-retry.ts: a transient refusal (429, 502-504, the network) is sent again while the card keeps loading.
      const res = await withTransientRetry(() => doFetch(NEXT_STEP_PATH, { method: 'POST', headers: headers(tok), body: JSON.stringify({ patientId }), signal: ac.signal }), (r) => r.status, ac.signal);
      const body = await readJson(res);
      card = res.status === 200 ? parseNextStep(body) : null;
      if (!card) notice = res.status === 401 ? SESSION_EXPIRED : res.status === 429 ? STATE_LINE.busy : res.status === 504 ? STATE_LINE.timeout : STATE_LINE.technical;
    } catch {
      notice = STATE_LINE.technical;
    }
    // Read-back: an item already decided in this encounter shows "Recorded" with its buttons closed. A failed or
    // malformed read-back pre-fills nothing; the engine stays the record.
    const decisions: Record<string, DecisionState> = {};
    const onCard = new Set(card?.items.map((i) => i.id) ?? []);
    if (card && !ac.signal.aborted) {
      for (const d of (await readBack(tok, ac.signal)) ?? []) {
        // Newest first: the first decision for an item in this encounter wins.
        if (!onCard.has(d.itemId) || decisions[d.itemId]) continue;
        decisions[d.itemId] = { status: 'recorded', line: null, request: toRequest(d, patientId) };
      }
    }
    if (ac.signal.aborted) return; // A7: a reply for an earlier patient or a cancelled request is dropped.
    cardFlight.current = null;
    // Union by itemId with what this screen already knows: a press recorded here (possibly after the read-back was
    // taken) wins over the snapshot, so a recorded item never reopens and a second press can never mint a new id.
    setView((v) => {
      if (v.context !== context) return v;
      // No valid replacement card: keep every saved press (and its idempotency key) until one can reconcile it (Codex P2).
      const mine = card ? Object.fromEntries(Object.entries(v.decisions).filter(([id]) => onCard.has(id))) : v.decisions;
      // The read-back wins over an unresolved press (the engine holds it); a press recorded here wins over the read-back.
      return { status: 'done', card, notice, decisions: { ...unresolvedOnly(mine), ...decisions, ...recordedOnly(mine) }, context };
    });
  }, [patientId, context, access, doFetch, readBack]);

  const send = useCallback(
    async (request: DecisionRequest) => {
      const itemId = request.itemId;
      if (decisionFlights.current.has(itemId)) return; // one decision request in flight per item
      const tok = access();
      if (!tok) {
        setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'retryable', request, line: SESSION_EXPIRED } } }));
        return;
      }
      const ac = new AbortController();
      decisionFlights.current.set(itemId, ac);
      setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'sending', request, line: null } } }));
      let out: ReturnType<typeof decisionOutcome>;
      try {
        const res = await doFetch(DECISIONS_PATH, { method: 'POST', headers: headers(tok), body: JSON.stringify(request), signal: ac.signal });
        out = decisionOutcome(res.status, await readJson(res), request.decisionId);
      } catch {
        out = { status: 'retryable', line: DECISION_LINE.technical };
      }
      if (ac.signal.aborted) return; // A7: the patient changed; this reply is never drawn.
      decisionFlights.current.delete(itemId);
      setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: out.status, request, line: out.line } } }));
    },
    [access, doFetch, context],
  );

  /**
   * Resend an earlier press. Within the engine's window the SAME body goes again (same decisionId, the idempotency key).
   * Past DECIDED_AT_STALE_MS the engine would refuse the old decidedAt (400) and neither control could recover, so first
   * ask the engine: if it already holds this decisionId the item is recorded; otherwise the press goes as a NEW decision
   * (new id, fresh decidedAt) — the old id was never recorded, so this cannot write one press twice.
   */
  const resend = useCallback(
    async (prev: DecisionRequest) => {
      const itemId = prev.itemId;
      const age = now().getTime() - new Date(prev.decidedAt).getTime();
      if (!(age > DECIDED_AT_STALE_MS)) return send(prev);
      if (decisionFlights.current.has(itemId)) return;
      const tok = access();
      if (!tok) {
        setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'retryable', request: prev, line: SESSION_EXPIRED } } }));
        return;
      }
      const ac = new AbortController();
      decisionFlights.current.set(itemId, ac);
      setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'sending', request: prev, line: null } } }));
      const list = await readBack(tok, ac.signal);
      if (ac.signal.aborted) return; // A7
      decisionFlights.current.delete(itemId);
      if (list === null) {
        // The engine could not say: nothing is sent, the line stays retryable.
        setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'retryable', request: prev, line: DECISION_LINE.technical } } }));
        return;
      }
      if (list.some((d) => d.decisionId === prev.decisionId)) {
        setView((v) => (v.context !== context ? v : { ...v, decisions: { ...v.decisions, [itemId]: { status: 'recorded', request: prev, line: null } } }));
        return;
      }
      await send({ ...prev, decisionId: newId(), decidedAt: now().toISOString() });
    },
    [access, context, newId, now, readBack, send],
  );

  /**
   * A physician press. Returns false when nothing was sent (no patient, no card, a missing reason, a closed item).
   * Same action and reason as a retryable earlier press = a retry, with the same decisionId. Anything else = a new id.
   */
  const decide = useCallback(
    (itemId: string, action: DecisionAction, reason?: string): boolean => {
      if (!patientId || !view.card || !view.card.items.some((i) => i.id === itemId)) return false;
      const why = reason?.trim() ?? '';
      if (action === 'not-indicated' && (!why || why.length > REASON_MAX)) return false; // a reason is required for "Not indicated"
      const prev = view.decisions[itemId];
      if (prev && prev.status !== 'retryable') return false; // sending, recorded or conflict: closed
      const sameAsBefore = prev && prev.request.action === action && (prev.request.reason ?? '') === (action === 'not-indicated' ? why : '');
      if (sameAsBefore) {
        void resend(prev.request);
        return true;
      }
      const request: DecisionRequest = { decisionId: newId(), patientId, encounterId: encounterId ?? null, card: 'next-step', itemId, action, ...(action === 'not-indicated' ? { reason: why } : {}), decidedAt: now().toISOString() };
      void send(request);
      return true;
    },
    [patientId, encounterId, view.card, view.decisions, newId, now, send, resend],
  );

  /** Resend the item's last retryable decision: unchanged (same decisionId) inside the engine's window; see resend(). */
  const retry = useCallback(
    (itemId: string): boolean => {
      const prev = view.decisions[itemId];
      if (!prev || prev.status !== 'retryable') return false;
      void resend(prev.request);
      return true;
    },
    [view.decisions, resend],
  );

  return { ...view, load, decide, retry };
}
